import CustomTooltip from '../../../shared/CustomTooltip';
import {
    Box,
    CardContent,
    Grid,
    Typography,
    Card,
    Skeleton,
} from '@mui/material';
import { Period } from '../../../enums/reports';
import React from 'react';
import { useGetOccupancyQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { InfoOutlined } from '@mui/icons-material';

interface SmallStatsProps {
    period: Period;
    module?: number;
    moduleLoading?: boolean;
}
const SmallStats = ({ period, module, moduleLoading }: SmallStatsProps) => {
    const { data: occupancy, isFetching } = useGetOccupancyQuery(
        {
            period,
            module,
        },
        { skip: moduleLoading },
    );

    return !moduleLoading ? (
        <>
            <Grid item xs={12} lg>
                <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ m: 1 }}>
                        <div>
                            <Typography
                                sx={{
                                    fontSize: '3rem',
                                    fontWeight: 'bold',
                                }}
                            >
                                {occupancy && occupancy.length > 0
                                    ? (
                                          occupancy.reduce(
                                              (sum: number, item: any) =>
                                                  sum +
                                                  item.availableNightsOccupancy,
                                              0,
                                          ) / occupancy.length
                                      ).toFixed(1)
                                    : 'N/A'}
                                %
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Average Occupancy (nights occupied){' '}
                                    <CustomTooltip title='Calculated as the number of reserved nights, divided by the number of available nights. Day reservations and reservations that fall outside of current availability are excluded from this number.'>
                                        <InfoOutlined
                                            sx={{ color: 'text.primary' }}
                                        />
                                    </CustomTooltip>
                                </Typography>
                            </Box>
                        </div>
                    </CardContent>
                </Card>
            </Grid>
            {!module && (
                <Grid item xs={12} lg>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ m: 1 }}>
                            <div>
                                <Typography
                                    sx={{
                                        fontSize: '3rem',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {occupancy && occupancy.length > 0
                                        ? (
                                              occupancy.reduce(
                                                  (sum: number, item: any) =>
                                                      sum + item.bookings,
                                                  0,
                                              ) / occupancy.length
                                          ).toFixed(1)
                                        : 'N/A'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        Average Reservations per Experience{' '}
                                        <CustomTooltip title='The total number of reservations for all experiences, divided by the number of experiences available.'>
                                            <InfoOutlined
                                                sx={{ color: 'text.primary' }}
                                            />
                                        </CustomTooltip>
                                    </Typography>
                                </Box>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            )}
            <Grid item xs={12} lg>
                <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ m: 1 }}>
                        <div>
                            <Typography
                                sx={{
                                    fontSize: '3rem',
                                    fontWeight: 'bold',
                                }}
                            >
                                {occupancy && occupancy.length > 0
                                    ? Math.round(
                                          occupancy.reduce(
                                              (sum: number, item: any) =>
                                                  sum + item.bookings,
                                              0,
                                          ),
                                      )
                                    : 0}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Total Reservations{' '}
                                    <CustomTooltip
                                        title={`The total number of reservations for ${
                                            module
                                                ? 'this experience'
                                                : 'all experiences'
                                        }.`}
                                    >
                                        <InfoOutlined
                                            sx={{ color: 'text.primary' }}
                                        />
                                    </CustomTooltip>
                                </Typography>
                            </Box>
                        </div>
                    </CardContent>
                </Card>
            </Grid>
            {module && (
                <Grid item xs={12} lg>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ m: 1 }}>
                            <div>
                                <Typography
                                    sx={{
                                        fontSize: '3rem',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {occupancy && occupancy.length > 0
                                        ? occupancy[0].availableNights -
                                          occupancy[0].availableOccupiedNights
                                        : 'N/A'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        Vacant Nights
                                    </Typography>
                                    <CustomTooltip
                                        title={`The number of unoccupied nights still available for reservations.`}
                                    >
                                        <InfoOutlined
                                            sx={{ color: 'text.primary' }}
                                        />
                                    </CustomTooltip>
                                </Box>
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
            )}
        </>
    ) : (
        <>
            <Grid item xs={12} lg={4}>
                <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ m: 1 }}>
                        <Skeleton variant='text' height={75} width={100} />
                        <Skeleton variant='text' width={300} />
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
                <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ m: 1 }}>
                        <Skeleton variant='text' height={75} width={100} />
                        <Skeleton variant='text' width={300} />
                    </CardContent>
                </Card>
            </Grid>
        </>
    );
};

export default SmallStats;
